/* useWaitingPreview — live thumbnail of the CALL-WAITING visitor.
   ---------------------------------------------------------------
   The waiting ring lives in its own Agora channel, so we join it with
   a throwaway subscriber-only uid (see backend preview_token endpoint)
   and subscribe to VIDEO ONLY — never audio, so the waiting visitor's
   voice can't bleed over the active call. Cleans up (leaves the
   channel) the moment the waiting slot clears or the banner unmounts,
   which is always before an "End & answer" join happens. */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import AgoraRTC from "agora-rtc-sdk-ng";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useWaitingPreview(waitingRing) {
  const [previewOn, setPreviewOn] = useState(false);
  const clientRef = useRef(null);
  const trackRef = useRef(null);
  const containerRef = useRef(null);

  const attachPreviewContainer = useCallback((el) => {
    containerRef.current = el;
    if (el && trackRef.current) {
      try { trackRef.current.play(el); } catch { /* track disposed */ }
    }
  }, []);

  const ringId = waitingRing?.ring_id || null;

  useEffect(() => {
    if (!ringId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/doorbell/rings/${ringId}/preview_token`, { withCredentials: true });
        if (cancelled) return;
        const client = AgoraRTC.createClient({ mode: "rtc", codec: "h264" });
        clientRef.current = client;
        client.on("user-published", async (u, mediaType) => {
          if (mediaType !== "video") return; // audio is deliberately ignored
          try {
            await client.subscribe(u, "video");
          } catch { return; }
          trackRef.current = u.videoTrack;
          setPreviewOn(true);
          if (containerRef.current) {
            try { u.videoTrack.play(containerRef.current); } catch { /* noop */ }
          }
        });
        client.on("user-unpublished", (u, mediaType) => {
          if (mediaType !== "video") return;
          try { trackRef.current?.stop(); } catch { /* noop */ }
          trackRef.current = null;
          setPreviewOn(false);
        });
        await client.join(r.data.agora_app_id, r.data.channel, r.data.token, r.data.uid);
      } catch (e) {
        // Thumbnail is best-effort — the banner falls back to the bell icon.

        console.warn("[Scan2Call] waiting-preview join failed:", e?.message || e);
      }
    })();
    return () => {
      cancelled = true;
      try { trackRef.current?.stop(); } catch { /* noop */ }
      trackRef.current = null;
      setPreviewOn(false);
      const c = clientRef.current;
      clientRef.current = null;
      if (c) { try { c.leave(); } catch { /* noop */ } }
    };
  }, [ringId]);

  return { previewOn, attachPreviewContainer };
}
